import { memo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { CircularProgress } from "@mui/material";

import {
  useLoadingContext,
  useNotificationContext,
  useUserDetailsContext,
} from "@zenith/context/global-context/hooks";
import {
  ApiResponseDTOUserDetailDTO,
  CommitProfileUpdateDTO,
  ProfileUpdateApiService,
  UserDetailsManagementRequestDTO,
  UserManagementApiService,
} from "@zenith/services/user";
import { ToastSnackbarComponent } from "@zenith/components";
import {
  CHANGES_SAVED_SUCCESSFULLY_TOAST_MESSAGE,
  CHANGES_SAVED_SUCCESSFULLY_TOAST_TITLE,
  CHANGES_SAVED_UNSUCCESSFULLY_TOAST_MESSAGE,
  CHANGES_SAVED_UNSUCCESSFULLY_TOAST_TITLE,
} from "@zenith/data/constants";
import { UserRole } from "@zenith/types";
import {
  EmployeeProfileComponent,
  MerchantProfileComponent,
  ProfilePhotoVerificationComponent,
  ProfileUpdateOtpComponent,
} from "@zenith/features/Profile";
import type { ProfileUpdatePurpose } from "@zenith/features/Profile";
import { PROFILE_MANAGEMENT, SETTINGS } from "@zenith/routes/paths";

const ProfileManagementPage = memo(function ProfileManagementPage() {
  const { userDetailsContext, setUserDetailsContext } = useUserDetailsContext();
  const { setOpen } = useNotificationContext();
  const { setLoading } = useLoadingContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [showOTP, setShowOTP] = useState<boolean>(false);
  const [showPhotoVerification, setShowPhotoVerification] =
    useState<boolean>(false);
  const [purpose, setPurpose] = useState<ProfileUpdatePurpose | null>(null);
  const [updateToken, setUpdateToken] = useState<string | null>(null);
  const [newValueForOtp, setNewValueForOtp] = useState<string>("");
  const [userFilledData, setUserFilledData] =
    useState<UserDetailsManagementRequestDTO | null>(null);
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  function updateProfile(body: UserDetailsManagementRequestDTO): Promise<void> {
    setUserDetailsContext({
      state: "LOADING",
    });
    setUserFilledData(body);
    setLoading(true);
    return UserManagementApiService.putUserManagement(
      userDetailsContext.id,
      body
    )
      .then((response) => {
        changesSavedSuccessfully(response);
      })
      .catch((error) => {
        notify(
          CHANGES_SAVED_UNSUCCESSFULLY_TOAST_TITLE,
          error?.body?.message || CHANGES_SAVED_UNSUCCESSFULLY_TOAST_MESSAGE,
          "error"
        );
      })
      .finally(() => {
        setLoading(false);
        setUserDetailsContext({
          state: "SUCCESS",
        });
      });
  }

  function updateProfileWithOTP(body: UserDetailsManagementRequestDTO): void {
    const emailChanged = Boolean(
      body.email && body.email !== userDetailsContext.email
    );
    const newPurpose: ProfileUpdatePurpose = emailChanged
      ? "UPDATE_EMAIL"
      : "UPDATE_PHONE";
    const newValue = emailChanged ? body.email ?? "" : body.phoneNumber ?? "";

    setPurpose(newPurpose);
    setNewValueForOtp(newValue);
    setUserFilledData(body);
    setShowOTP(true);
  }

  function handleOtpVerified(token: string): void {
    setUpdateToken(token);
    setShowOTP(false);
    setShowPhotoVerification(true);
  }

  function handlePhotoVerified(jobId: string): void {
    if (!updateToken || !purpose) return;
    setShowPhotoVerification(false);
    setLoading(true);

    const dto: CommitProfileUpdateDTO = {
      jobId,
      updateToken,
      purpose:
        purpose === "UPDATE_EMAIL"
          ? CommitProfileUpdateDTO.purpose.UPDATE_EMAIL
          : CommitProfileUpdateDTO.purpose.UPDATE_PHONE,
    };

    const commitCall =
      purpose === "UPDATE_EMAIL"
        ? ProfileUpdateApiService.commitEmailUpdate(dto)
        : ProfileUpdateApiService.commitPhoneUpdate(dto);

    commitCall
      .then(() => {
        if (purpose === "UPDATE_EMAIL") {
          setUserDetailsContext({ email: newValueForOtp });
        } else {
          setUserDetailsContext({ phoneNumber: newValueForOtp });
        }
        setUserFilledData(null);
        setPurpose(null);
        setUpdateToken(null);
        setNewValueForOtp("");
        const successMessage =
          purpose === "UPDATE_EMAIL"
            ? "Your email has now been successfully updated."
            : "Your phone number has now been successfully updated.";
        navigate(PROFILE_MANAGEMENT, {
          state: {
            notification: {
              title: CHANGES_SAVED_SUCCESSFULLY_TOAST_TITLE,
              message: successMessage,
              status: "success",
            },
          },
        });
      })
      .catch((error) => {
        const code = error?.body?.internalCode ?? "";
        if (code === "ZENZ-USER-055") {
          setUpdateToken(null);
          setShowOTP(true);
          notify(
            "Session Expired",
            "Your verification session has expired. Please verify again.",
            "warning"
          );
        } else {
          notify(
            CHANGES_SAVED_UNSUCCESSFULLY_TOAST_TITLE,
            error?.body?.message || CHANGES_SAVED_UNSUCCESSFULLY_TOAST_MESSAGE,
            "error"
          );
        }
      })
      .finally(() => setLoading(false));
  }

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  function changesSavedSuccessfully(
    response: ApiResponseDTOUserDetailDTO
  ): void {
    setUserDetailsContext({
      email: response.data?.email ?? userDetailsContext.email,
      phoneNumber: response.data?.phoneNumber ?? userDetailsContext.phoneNumber,
      tradingName: response.data?.tradingName ?? userDetailsContext.tradingName,
    });
    setUserFilledData(null);

    navigate(SETTINGS, {
      state: {
        notification: {
          title: CHANGES_SAVED_SUCCESSFULLY_TOAST_TITLE,
          message: CHANGES_SAVED_SUCCESSFULLY_TOAST_MESSAGE,
          status: "success",
        },
      },
    });
  }

  function getProfileView(): JSX.Element {
    if (userDetailsContext.state === "LOADING") {
      return (
        <div className="flex h-80 w-full items-center justify-center text-center">
          <CircularProgress className={"text-primary-300"} size={"3rem"} />
        </div>
      );
    }

    switch (userDetailsContext.role) {
      case UserRole.ADMINISTRATOR:
        return (
          <MerchantProfileComponent
            updateProfile={updateProfile}
            updateProfileWithOTP={updateProfileWithOTP}
            userFilledData={userFilledData}
          />
        );

      case UserRole.EMPLOYEE:
        return (
          <EmployeeProfileComponent
            updateProfile={updateProfile}
            updateProfileWithOTP={updateProfileWithOTP}
            userFilledData={userFilledData}
          />
        );

      default:
        throw new Error("User role not mapped");
    }
  }

  return (
    <>
      <ToastSnackbarComponent
        horizontal="center"
        status={toasterData.status}
        vertical="top"
        hasAutoHideDuration
        size="lg"
        title={toasterData.title}
        text={toasterData.message}
      />
      <div className="ml:0 lg:ml-36">
        {showOTP && purpose ? (
          <div className="mt-14 max-w-[437px]">
            <ProfileUpdateOtpComponent
              newValue={newValueForOtp}
              purpose={purpose}
              onVerified={handleOtpVerified}
              onGoBack={() => setShowOTP(false)}
            />
          </div>
        ) : showPhotoVerification ? (
          <div className="mt-14 max-w-[437px]">
            <ProfilePhotoVerificationComponent
              onPhotoVerified={handlePhotoVerified}
              onGoBack={() => {
                setShowPhotoVerification(false);
                setShowOTP(true);
              }}
            />
          </div>
        ) : (
          <div className="mt-12 max-w-[437px]">{getProfileView()}</div>
        )}
      </div>
    </>
  );
});

export default ProfileManagementPage;
